import { signerFieldPrompt } from './fields'
import type { FieldKind } from './api'

/**
 * Boxes that wait on another person's box in a sent document, found before
 * Save as template is pressed.
 *
 * Before b29a80f the sending page offered every signer's boxes under "Show
 * only when", and the server accepted the send. Since then the server
 * refuses such a rule (400 bad_show_when), and Save as template on such a
 * document is refused with it. These find each such box, so the sheet can
 * name it, hold Save, and say how to get a document that saves (review 5:
 * web-4).
 *
 * A stored template is not judged here. The server reads every stored
 * template and says when one cannot be sent (needs_rebuilding on GET
 * /api/templates/{id}), for this rule and for a role with nothing to sign,
 * so the template page shows the server's sentence and nothing else: one
 * product, one opinion about which template is broken (R7-web). Only the
 * Save as template sheet still needs a check of its own, because the server
 * can say nothing about a template that has not been saved.
 */
export interface CrossPersonRule {
  /** The box that waits, and who fills it. */
  prompt: string
  owner: string
  /** Who fills the box it waits on. */
  controller: string
}

interface RuleField {
  id: string
  kind: FieldKind
  label?: string
  show_when?: { field_id: string }
}

/**
 * A sent document's boxes that wait on another signer's box, as Save as
 * template reads them: one role per signer participation, and a rule whose
 * box belongs to nobody who signs is left behind rather than refused
 * (internal/store/template.go rolesFromEnvelopeTx).
 */
export function envelopeCrossPersonRules(
  participations: readonly { id: string; name: string; role: string }[],
  fields: readonly (RuleField & { participation_id: string })[],
): CrossPersonRule[] {
  const signers = new Map(participations.filter((p) => p.role === 'signer').map((p) => [p.id, p.name]))
  const byID = new Map(fields.map((f) => [f.id, f]))
  return fields.flatMap((f) => {
    if (!signers.has(f.participation_id)) return []
    const ctrl = f.show_when?.field_id ? byID.get(f.show_when.field_id) : undefined
    if (!ctrl || !signers.has(ctrl.participation_id) || ctrl.participation_id === f.participation_id) return []
    return [{
      prompt: signerFieldPrompt(f.label ?? '', f.kind),
      owner: signers.get(f.participation_id) ?? '',
      controller: signers.get(ctrl.participation_id) ?? '',
    }]
  })
}

/** What is wrong with one such box, in the sending page's words for the same fault. */
export function crossPersonRuleWords(r: CrossPersonRule): string {
  return `${r.owner}'s “${r.prompt}” box is shown only when ${r.controller} answers one of their boxes, but ${r.owner} sees only their own boxes when signing, so it would never be shown.`
}

/**
 * What the Save as template sheet says it saves. A template keeps one role
 * per signer and their boxes (store rolesFromEnvelopeTx); a witness, a
 * viewer and a copy are left behind, and "the boxes on it" promised all of
 * them (review 6, docs-2). One sentence for the web's sheet and the app's,
 * which imports it through mobile/src/shared/web.ts.
 */
export const SAVE_TEMPLATE_INTRO =
  "Saves this document and the signers' boxes on it. A witness, and anyone who only views or gets a copy, is not saved."

/** What to do about a document that holds such a box, before it can be saved as a template. */
export const ENVELOPE_CROSS_PERSON_REMEDY =
  "This document cannot be saved as a template as it is. Use Send again, and under “Show only when” choose one of the same person's boxes or “Always shown”. Send it, and save that document as a template."

/**
 * The server's refusal of a "Show only when" rule (400 bad_show_when), for a
 * fault the sheet did not find first. The server's sentence names the person
 * and the box (R7-server) but not the next step, and the sheet has no way to
 * the box, so the next step is added to it.
 */
export function refusedRuleWords(code: string | undefined, message: string, remedy: string): string | null {
  if (code !== 'bad_show_when') return null
  return `${message} ${remedy}`
}
